import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import { useScreenPermissions } from './useScreenPermissions';

import PurchaseOrderViewModal from './PurchaseOrderViewModal';
import MaterialReceiptViewModal from './MaterialReceiptViewModal';
import SalesOrderViewModal from './SalesOrderViewModal';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const TrackDocumentIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const HidePanelIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

// Modern Error Alert Icon
const ErrorAlertIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" stroke="currentColor" fill="none" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// Document type icons
const SalesOrderIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const PurchaseOrderIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2l3.54 6.54-2.32 2.32L6 22h12l-1.22-11.14-2.32-2.32L18 2H6z"></path>
    <line x1="12" y1="22" x2="12" y2="16"></line>
    <line x1="9" y1="16" x2="15" y2="16"></line>
  </svg>
);

const MRNIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const DeliveryNoteIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

// Box Icon for Batch Modal
const BoxIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

// Barcode Icon for Serial Modal
const BarcodeIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7h18M3 12h18M3 17h18"></path>
  </svg>
);

// Modern Error Popup Component
const ErrorPopup = ({ message, isVisible, onClose }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 4000); // Auto-close after 4 seconds
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop without blur */}
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose}></div>
      
      {/* Modern Popup Card */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full transform transition-all duration-300 animate-fadeIn">
        {/* Decorative top border */}
        <div className="h-1 w-full bg-gradient-to-r from-red-500 to-red-600 rounded-t-2xl"></div>
        
        <div className="p-6">
          <div className="flex items-start">
            {/* Icon with gradient background */}
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
                <ErrorAlertIcon className="h-6 w-6 text-white" />
              </div>
            </div>
            
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-bold text-gray-900">Access Denied</h3>
              <div className="mt-2">
                <p className="text-sm text-gray-600">{message}</p>
              </div>
            </div>
            
            {/* Close button */}
            <div className="ml-4 flex-shrink-0 flex">
              <button
                className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                onClick={onClose}
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 border border-transparent rounded-lg shadow-md hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all"
              onClick={onClose}
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Batch Info Modal Component
const BatchInfoModal = ({ isOpen, onClose, batches = [], productName = "" }) => {
  console.log('BatchInfoModal received batches:', batches);
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="bg-gray-300 p-1.5 rounded">
                <BoxIcon className="text-gray-700 text-sm" />
              </div>
              <h3 className="text-base font-medium text-gray-800">Batch Details</h3>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
          <div className="flex items-center">
            <span className="text-xs font-medium text-gray-600 mr-1">Product:</span>
            <span className="text-xs font-medium text-gray-800 ">{productName || "N/A"}</span>
          </div>
        </div>
        
        {/* Batch Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-0">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {batches.length > 0 ? (
                  batches.map((batch, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
                          {batch.BatchNumber || batch.batchNumber || batch.TrackingNumber || 'N/A'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs text-gray-700">
                          {batch.ExpiryDate || batch.expiryDate 
                            ? dayjs(batch.ExpiryDate || batch.expiryDate).format('YYYY-MM-DD') 
                            : '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-800">
                          {batch.Quantity || batch.quantity || '0'}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <BoxIcon className="text-gray-300 text-xl mb-1" />
                        <span>No batch information available</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">
              {batches.length} batch{batches.length !== 1 ? 'es' : ''} displayed
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-500 transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Serial Info Modal Component
const SerialInfoModal = ({ isOpen, onClose, serialNumbers = [], productName = "", locationCode = "" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[80vh] flex flex-col border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="bg-gray-300 p-1.5 rounded">
                <BarcodeIcon className="text-gray-700 text-sm" />
              </div>
              <h3 className="text-base font-medium text-gray-800">Serial Numbers</h3>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Product:</span>
              <span className="text-xs font-medium text-gray-800">{productName || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Location:</span>
              <span className="text-xs font-medium text-gray-800">{locationCode || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Count:</span>
              <span className="text-xs font-medium text-gray-800">{serialNumbers.length}</span>
            </div>
          </div>
        </div>
        
        {/* Serial Numbers Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-0">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {serialNumbers.length > 0 ? (
                  serialNumbers.map((serial, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-mono font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
                          {serial}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                        {productName || "N/A"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {locationCode || "N/A"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <BarcodeIcon className="text-gray-300 text-xl mb-1" />
                        <span>No serial numbers available</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">
              {serialNumbers.length} serial number{serialNumbers.length !== 1 ? 's' : ''} displayed
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-500 transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const API_URL = '/api';

const DeliveryNoteTrackingTree = ({ 
  data, 
  trackingLoading, 
  trackingError,
  permissions,
  onDocumentClick,
  onCloseTrackingPanel
}) => {
  if (!data) return null;
  
  const { linkedSalesOrders, linkedPurchaseOrders, mrns, deliveryNote } = data;
  const { canViewPO, canViewMRN, canViewDeliveryNote, canViewSales, showErrorPopup } = permissions;
  
  const StatusBadge = ({ status }) => {
    const statusStyle = status === 'Completed' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-yellow-100 text-yellow-800';
      
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${statusStyle}`}>
        {status}
      </span>
    );
  };
  
  // Enhanced Document Node Component with click handling
  const DocumentNode = ({ 
    type, 
    number, 
    date, 
    status, 
    isPending = false,
    isCenter = false,
    onClick
  }) => {
    const getIcon = () => {
      switch(type) {
        case 'sales':
          return <SalesOrderIcon className="text-white" />;
        case 'purchase':
          return <PurchaseOrderIcon className="text-white" />;
        case 'mrn':
          return <MRNIcon className={isPending ? "text-gray-400" : "text-white"} />;
        case 'deliveryNote':
          return <DeliveryNoteIcon className={isPending ? "text-gray-400" : "text-white"} />;
        default:
          return null;
      }
    };
    
    const getColorClass = () => {
      if (isPending) return 'bg-gray-200';
      switch(type) {
        case 'sales':
          return 'bg-purple-500';
        case 'purchase':
          return 'bg-blue-500';
        case 'mrn':
          return 'bg-green-500';
        case 'deliveryNote':
          return 'bg-yellow-500';
        default:
          return 'bg-gray-500';
      }
    };
    
    const isClickable = (type === 'purchase' && canViewPO && !isPending) || 
                       (type === 'mrn' && canViewMRN && !isPending) ||
                       (type === 'deliveryNote' && canViewDeliveryNote && !isPending) ||
                       (type === 'sales' && canViewSales && !isPending);
    
    const handleClick = () => {
      if (isClickable && onClick) {
        onClick(type, number);
      } else if (!isClickable && !isPending) {
        if (type === 'purchase') {
          showErrorPopup("You don't have permission to view Purchase Orders");
        } else if (type === 'mrn') {
          showErrorPopup("You don't have permission to view MRNs");
        } else if (type === 'deliveryNote') {
          showErrorPopup("You don't have permission to view Delivery Notes");
        } else if (type === 'sales') {
          showErrorPopup("You don't have permission to view Sales Orders");
        }
      }
    };
    
    return (
      <div 
        className={`flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity ${isCenter ? 'mb-8' : ''}`}
        onClick={handleClick}
        title={isClickable ? `View ${type === 'purchase' ? 'Purchase Order' : type === 'mrn' ? 'MRN' : type === 'sales' ? 'Sales Order' : 'Delivery Note'} in new tab` : 
              isPending ? 'Not created yet' : 'No permission to view'}
      >
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getColorClass()}`}>
          {getIcon()}
        </div>
        <div className={`mt-2 text-center ${isPending ? 'text-gray-400' : 'text-gray-800'}`}>
          <div className="font-medium text-sm">
            {type === 'sales' && 'Sales Order'}
            {type === 'purchase' && 'Purchase Order'}
            {type === 'mrn' && 'MRN'}
            {type === 'deliveryNote' && 'Delivery Note'}
          </div>
          <div className="font-semibold">{number}</div>
          {date && !isPending && (
            <div className="text-xs text-gray-500 mt-1">
              {dayjs(date).format('MMM DD, YYYY')}
            </div>
          )}
          {status && !isPending && <StatusBadge status={status} className="mt-2" />}
          {isPending && (
            <div className="text-xs text-gray-400 italic mt-2">Not created yet</div>
          )}
          {!isClickable && !isPending && (
            <div className="text-xs text-red-500 italic mt-1">No permission</div>
          )}
        </div>
      </div>
    );
  };
  
  // Improved Connector from PO to multiple Sales Orders and MRNs
  const PoToMultipleChildrenConnector = ({ soCount, mrnCount }) => {
    return (
      <div className="relative w-full h-24 flex items-center justify-center">
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Vertical line from PO to branching point */}
          <line 
            x1="50%" 
            y1="0" 
            x2="50%" 
            y2="30" 
            stroke="#4B5563" 
            strokeWidth="1"
          />
          
          {/* Horizontal line for branching */}
          <line 
            x1="10%" 
            y1="30" 
            x2="90%" 
            y2="30" 
            stroke="#4B5563" 
            strokeWidth="1"
          />
          
          {/* Lines to each Sales Order */}
          {[...Array(soCount)].map((_, i) => {
            const x = 10 + (80 / (soCount + (mrnCount > 0 ? 1 : 0))) * (i + 0.5);
            return (
              <React.Fragment key={`so-line-${i}`}>
                <line 
                  x1={`${x}%`} 
                  y1="30" 
                  x2={`${x}%`} 
                  y2="90" 
                  stroke="#4B5563" 
                  strokeWidth="1"
                />
                {/* Arrowhead for Sales Order */}
                <polygon 
                  points={`${x},90 ${parseFloat(x) - 1.5},83 ${parseFloat(x) + 1.5},83`} 
                  fill="#4B5563" 
                />
              </React.Fragment>
            );
          })}
          
          {/* Line to MRNs group (if any) */}
          {mrnCount > 0 && (
            <React.Fragment>
              <line 
                x1="90%" 
                y1="30" 
                x2="90%" 
                y2="90" 
                stroke="#4B5563" 
                strokeWidth="1"
              />
              {/* Arrowhead for MRNs */}
              <polygon 
                points="90,90 88,83 92,83" 
                fill="#4B5563" 
              />
            </React.Fragment>
          )}
        </svg>
      </div>
    );
  };
  
  // Improved Connector from Sales Order to Delivery Note
  const SoToDeliveryNoteConnector = () => {
    return (
      <div className="relative w-full h-16 flex items-center justify-center">
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Vertical line from Sales Order */}
          <line 
            x1="50%" 
            y1="0" 
            x2="50%" 
            y2="85%" 
            stroke="#4B5563" 
            strokeWidth="1"
          />
          
          {/* Arrowhead */}
          <polygon
            points="50,100 45,90 55,90" 
            fill="#4B5563" 
          />
        </svg>
      </div>
    );
  };
  
  // Group MRNs by purchase order
  const mrnsByPO = {};
  if (mrns) {
    mrns.forEach(mrn => {
      if (!mrnsByPO[mrn.purchaseOrderNumber]) {
        mrnsByPO[mrn.purchaseOrderNumber] = [];
      }
      mrnsByPO[mrn.purchaseOrderNumber].push(mrn);
    });
  }
  
  // Create mappings between POs and SOs based on MRN links
  const poToSOs = {}; // PO number -> array of SO numbers
  const soToPOs = {}; // SO number -> array of PO objects
  
  // Initialize mappings
  linkedSalesOrders.forEach(so => {
    soToPOs[so.salesOrderNumber] = [];
  });
  
  linkedPurchaseOrders.forEach(po => {
    poToSOs[po.purchaseOrderNumber] = [];
  });
  
  // Populate mappings using MRN data
  mrns.forEach(mrn => {
    const poNumber = mrn.purchaseOrderNumber;
    if (mrn.items && mrn.items.length > 0) {
      mrn.items.forEach(item => {
        if (item.linkedDocumentNumber) {
          const soNumber = item.linkedDocumentNumber;
          
          // Add SO to PO's list
          if (!poToSOs[poNumber].includes(soNumber)) {
            poToSOs[poNumber].push(soNumber);
          }
          
          // Add PO to SO's list
          const po = linkedPurchaseOrders.find(p => p.purchaseOrderNumber === poNumber);
          if (po && !soToPOs[soNumber].find(p => p.purchaseOrderNumber === poNumber)) {
            soToPOs[soNumber].push(po);
          }
        }
      });
    }
  });
  
  // Also check for product matches as a fallback
  linkedSalesOrders.forEach(so => {
    linkedPurchaseOrders.forEach(po => {
      // Check if this PO is already linked to this SO
      const alreadyLinked = soToPOs[so.salesOrderNumber].some(p => p.purchaseOrderNumber === po.purchaseOrderNumber);
      
      if (!alreadyLinked) {
        // Check if this PO is linked to the SO by matching products
        const hasMatchingProducts = po.items.some(poItem => 
          so.items.some(soItem => 
            poItem.productCode === soItem.productCode && 
            poItem.orderedQuantity === soItem.quantity
          )
        );
        
        if (hasMatchingProducts) {
          // Add SO to PO's list
          if (!poToSOs[po.purchaseOrderNumber].includes(so.salesOrderNumber)) {
            poToSOs[po.purchaseOrderNumber].push(so.salesOrderNumber);
          }
          
          // Add PO to SO's list
          soToPOs[so.salesOrderNumber].push(po);
        }
      }
    });
  });
  
  // Separate sales orders into those with POs and those without
  const salesOrdersWithPO = [];
  const salesOrdersWithoutPO = [];
  
  linkedSalesOrders.forEach(so => {
    if (soToPOs[so.salesOrderNumber] && soToPOs[so.salesOrderNumber].length > 0) {
      salesOrdersWithPO.push(so);
    } else {
      salesOrdersWithoutPO.push(so);
    }
  });
  
  return (
    <div className="bg-white h-full">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-[#384959]">Document Tracking</h3>
        <button
          onClick={onCloseTrackingPanel}
          className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
          title="Hide Panel"
        >
          <HidePanelIcon size={20} />
        </button>
      </div>
      
      {trackingLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading tracking information...</p>
          </div>
        </div>
      ) : trackingError ? (
        <div className="bg-yellow-50 text-yellow-700 p-4 rounded-lg mb-6 border border-yellow-100">
          Tracking Error: {trackingError}
        </div>
      ) : data ? (
        <div className="flex flex-col items-center space-y-8">
          {/* Section 1: Purchase Orders with their connections */}
          {linkedPurchaseOrders.length > 0 && (
            <div className="w-full">
              <div className="text-center text-sm font-medium text-gray-600 mb-4">Purchase Orders</div>
              
              <div className="flex justify-center space-x-8">
                {linkedPurchaseOrders.map(po => {
                  const linkedSOs = poToSOs[po.purchaseOrderNumber] || [];
                  const hasMRNs = mrnsByPO[po.purchaseOrderNumber] && mrnsByPO[po.purchaseOrderNumber].length > 0;
                  
                  return (
                    <div key={po.purchaseOrderNumber} className="flex flex-col items-center w-full">
                      {/* Purchase Order Node */}
                      <DocumentNode 
                        type="purchase"
                        number={po.purchaseOrderNumber}
                        date={po.purchaseOrderDate}
                        status={po.status}
                        onClick={onDocumentClick}
                      />
                      
                      {/* Connector from PO to Sales Orders and MRNs */}
                      <PoToMultipleChildrenConnector 
                        soCount={linkedSOs.length} 
                        mrnCount={hasMRNs ? 1 : 0} 
                      />
                      
                      {/* Sales Orders and MRNs for this PO */}
                      <div className="flex justify-between w-full mt-4">
                        {/* Sales Orders Branch */}
                        <div className="flex flex-col items-center w-3/4">
                          <div className="text-center text-xs font-medium text-gray-500 mb-2">Sales Orders</div>
                          
                          {linkedSOs.length > 0 ? (
                            <div className="flex justify-center space-x-4">
                              {linkedSOs.map(soNumber => {
                                const so = linkedSalesOrders.find(s => s.salesOrderNumber === soNumber);
                                return so ? (
                                  <div key={so.salesOrderNumber} className="flex flex-col items-center">
                                    <DocumentNode 
                                      type="sales"
                                      number={so.salesOrderNumber}
                                      date={so.salesOrderDate}
                                      status={so.status}
                                      onClick={onDocumentClick}
                                    />
                                    
                                    {/* Connector from Sales Order to Delivery Note */}
                                    <SoToDeliveryNoteConnector />
                                    
                                    {/* Delivery Note Node */}
                                    <DocumentNode 
                                      type="deliveryNote"
                                      number={deliveryNote.deliveryNoteNo}
                                      date={deliveryNote.deliveryNoteDate}
                                      status={deliveryNote.status}
                                      onClick={onDocumentClick}
                                    />
                                  </div>
                                ) : null;
                              })}
                            </div>
                          ) : (
                            <DocumentNode 
                              type="sales"
                              number="SO"
                              isPending={true}
                            />
                          )}
                        </div>
                        
                        {/* MRNs Branch */}
                        {hasMRNs && (
                          <div className="flex flex-col items-center w-1/4">
                            <div className="text-center text-xs font-medium text-gray-500 mb-2">MRNs</div>
                            
                            <div className="flex flex-col space-y-2">
                              {mrnsByPO[po.purchaseOrderNumber].map(mrn => (
                                <DocumentNode 
                                  key={mrn.mrnNumber}
                                  type="mrn"
                                  number={mrn.mrnNumber}
                                  date={mrn.mrnDate}
                                  status={typeof mrn.status === 'boolean' ? (mrn.status ? "Completed" : "Pending") : mrn.status}
                                  onClick={onDocumentClick}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Section 2: Sales Orders without Purchase Orders */}
          {salesOrdersWithoutPO.length > 0 && (
            <div className="w-full">
              <div className="text-center text-sm font-medium text-gray-600 mb-4">Sales Orders without Purchase Orders</div>
              
              <div className="flex justify-center space-x-8">
                {salesOrdersWithoutPO.map(so => (
                  <div key={so.salesOrderNumber} className="flex flex-col items-center">
                    {/* Sales Order Node */}
                    <DocumentNode 
                      type="sales"
                      number={so.salesOrderNumber}
                      date={so.salesOrderDate}
                      status={so.status}
                      onClick={onDocumentClick}
                    />
                    
                    {/* Connector from Sales Order to Delivery Note */}
                    <SoToDeliveryNoteConnector />
                    
                    {/* Delivery Note Node */}
                    <DocumentNode 
                      type="deliveryNote"
                      number={deliveryNote.deliveryNoteNo}
                      date={deliveryNote.deliveryNoteDate}
                      status={deliveryNote.status}
                      onClick={onDocumentClick}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* If no sales orders at all */}
          {linkedSalesOrders.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              No sales orders linked to this delivery note
            </div>
          )}
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">
          No tracking data available
        </div>
      )}
    </div>
  );
};

const InvoiceViewModal = ({ open, onClose, deliveryNoteId }) => {
  const [deliveryNoteData, setDeliveryNoteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Tracking state
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState(null);
  const [showTrackingPanel, setShowTrackingPanel] = useState(false);
  
  // State for document modals
  const [activeDocumentTab, setActiveDocumentTab] = useState(null);

  // State for error popup
  const [errorPopup, setErrorPopup] = useState({
    isVisible: false,
    message: ''
  });

  // State for batch and serial modals
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [currentBatches, setCurrentBatches] = useState([]);
  const [currentSerials, setCurrentSerials] = useState([]);
  const [currentProductName, setCurrentProductName] = useState('');
  const [currentLocationCode, setCurrentLocationCode] = useState('');

  const [screenConfig, setScreenConfig] = useState(null);
  const SCREEN_NAME = 'DeliveryNote';

  // Add permissions hook
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  
  // Calculate permissions for MRN, PO and Sales Order
  const canViewMRN = hasFullAccess || hasPermission('MRN_MASTER', 'CanView');
  const canViewPO = hasFullAccess || hasPermission('PURCHASE_ORDER', 'CanView');
  const canViewDeliveryNote = hasFullAccess || hasPermission('DELIVERY_NOTE', 'CanView');
  const canViewSales = hasFullAccess || hasPermission('SALES_ORDER', 'CanView');

  // Function to show the error popup
  const showErrorPopup = (message) => {
    setErrorPopup({
      isVisible: true,
      message
    });
  };
  
  // Function to hide the error popup
  const hideErrorPopup = () => {
    setErrorPopup({
      isVisible: false,
      message: ''
    });
  };

  // Handle document click
  const handleDocumentClick = (docType, docNumber) => {
    console.log(`Document clicked: ${docType}, Number: ${docNumber}`);
    if (docType === 'purchase') {
      setActiveDocumentTab({
        type: 'purchase',
        number: docNumber,
        purchaseOrderNumber: docNumber,
        PurchaseOrderNumber: docNumber
      });
    } else if (docType === 'mrn') {
      setActiveDocumentTab({
        type: 'mrn',
        number: docNumber,
        mrnNumber: docNumber,
        MRNNumber: docNumber
      });
    } else if (docType === 'sales') {
      setActiveDocumentTab({
        type: 'sales',
        number: docNumber,
        salesOrderNumber: docNumber,
        SalesOrderNumber: docNumber
      });
    } else if (docType === 'deliveryNote') {
      setActiveDocumentTab({
        type: 'deliveryNote',
        number: docNumber,
        deliveryNoteNo: docNumber,
        DeliveryNoteNo: docNumber
      });
    }
  };

  // Handle viewing batch details
  const handleViewBatches = (batches, productName) => {
    console.log('Raw batches data:', batches);
    
    // Map the tracking details to the expected format for BatchInfoModal
    const formattedBatches = batches.map(batch => ({
      BatchNumber: batch.TrackingNumber || batch.batchNumber || batch.BatchNumber || 'N/A',
      ExpiryDate: batch.ExpiryDate || batch.expiryDate || null,
      Quantity: batch.Quantity || batch.quantity || 0
    }));
    
    console.log('Formatted batches:', formattedBatches);
    
    setCurrentBatches(formattedBatches);
    setCurrentProductName(productName);
    setShowBatchModal(true);
  };

  // Handle viewing serial details
  const handleViewSerials = (serials, productName, locationCode) => {
    console.log('Raw serials data:', serials);
    
    // Map the tracking details to the expected format for SerialInfoModal
    const formattedSerials = serials.map(serial => 
      typeof serial === 'string' ? serial : 
      serial.SerialNumber || serial.TrackingNumber || serial.serialNumber || 'N/A'
    );
    
    console.log('Formatted serials:', formattedSerials);
    
    setCurrentSerials(formattedSerials);
    setCurrentProductName(productName);
    setCurrentLocationCode(locationCode);
    setShowSerialModal(true);
  };

  // Function to fetch item tracking details
  const fetchItemTrackingDetails = async (deliveryNoteNo, deliveryNoteItemCode) => {
    try {
      const response = await fetch(`${API_URL}/delivery-note/${deliveryNoteNo}/item/${deliveryNoteItemCode}/tracking`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        console.error('Failed to fetch tracking details:', response.status, response.statusText);
        return [];
      }
      
      const data = await response.json();
      console.log('Fetched tracking details for item:', deliveryNoteItemCode, data);
      
      // Handle different response formats
      if (Array.isArray(data)) {
        return data;
      } else if (data && data.trackingDetails && Array.isArray(data.trackingDetails)) {
        return data.trackingDetails;
      } else if (data && data.batches && Array.isArray(data.batches)) {
        return data.batches.map(batch => ({
          ...batch,
          TrackingType: 'batch'
        }));
      } else if (data && data.serials && Array.isArray(data.serials)) {
        return data.serials.map(serial => ({
          ...serial,
          TrackingType: 'serial'
        }));
      } else if (data && typeof data === 'object') {
        // If it's a single object, convert to array
        return [data];
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching tracking details:', error);
      return [];
    }
  };

  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };

  const shouldShowDepartmentOnHeader = (cfg) => {
    if (!cfg) return false;
    const showHeader = truthy(cfg.ShowOnHeader) || truthy(cfg.showOnHeader);
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    return showHeader && showScreen;
  };

  const shouldShowDepartmentOnItems = (cfg) => {
    if (!cfg) return false;
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    const showItem = truthy(cfg.ShowOnItem) || truthy(cfg.showOnItem);
    return showScreen && showItem;
  };

  const fetchScreenConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        let body = null;
        try { body = await res.json(); } catch (e) {}
        const msg = body?.message ? body.message : `Server returned ${res.status}`;
        throw new Error(msg);
      }
      const data = await res.json();
      const cfg = Array.isArray(data) ? (data[0] || null) : data;
      console.log('Screen info.....', cfg);
      setScreenConfig(cfg);
      return cfg;
    } catch (err) {
      console.error('fetchScreenConfig error:', err);
      setScreenConfig(null);
      return null;
    }
  };

  useEffect(() => {
    console.log('InvoiceViewModal useEffect called with:', { open, deliveryNoteId });
    if (open && deliveryNoteId) {
      setIsMaximized(false);
      setShowTrackingPanel(false);
      setActiveDocumentTab(null); // Reset document tab state
      fetchScreenConfig();
      fetchDeliveryNoteData();
      
      // Extract the delivery note number
      let deliveryNoteNumber;
      if (typeof deliveryNoteId === 'string') {
        deliveryNoteNumber = deliveryNoteId;
      } else if (deliveryNoteId && typeof deliveryNoteId === 'object') {
        deliveryNoteNumber = deliveryNoteId.deliveryNoteNo || deliveryNoteId.DeliveryNoteNo || deliveryNoteId.number || deliveryNoteId.Number;
      }
      
      if (deliveryNoteNumber) {
        fetchTrackingData(deliveryNoteNumber);
      }
    } else {
      setDeliveryNoteData(null);
      setError(null);
      setCustomerInfo(null);
      setScreenConfig(null);
      setIsMaximized(false);
      setTrackingData(null);
    }
  }, [open, deliveryNoteId]);
  
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  const toggleTrackingPanel = () => {
    const newState = !showTrackingPanel;
    setShowTrackingPanel(newState);
    // Maximize when tracking panel is opened
    if (newState && !isMaximized) {
      setIsMaximized(true);
    }
  };
  
  const fetchDeliveryNoteData = async () => {
    try {
      setLoading(true);
      setError(null);
     
      const token = localStorage.getItem('token');
      console.log('Auth token:', token ? 'Token present' : 'No token');
     
      // Extract the delivery note number from the deliveryNoteId object
      let deliveryNoteNumber;
      if (typeof deliveryNoteId === 'string') {
        deliveryNoteNumber = deliveryNoteId;
      } else if (deliveryNoteId && typeof deliveryNoteId === 'object') {
        // Try different possible properties where the delivery note number might be stored
        deliveryNoteNumber = deliveryNoteId.deliveryNoteNo || deliveryNoteId.DeliveryNoteNo || deliveryNoteId.number || deliveryNoteId.Number;
      }
      
      if (!deliveryNoteNumber) {
        console.error('Invalid deliveryNoteId object:', deliveryNoteId);
        throw new Error('Delivery note number not provided');
      }
      
      const url = `${API_URL}/delivery-note/${deliveryNoteNumber}`;
      console.log('Fetching from URL:', url);
     
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
     
      console.log('Response status:', response.status);
     
      // Check if the response is OK before parsing JSON
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Delivery note not found: ${deliveryNoteNumber}`);
        }
        // Try to get error message from response
        const errorText = await response.text();
        console.error('Error response text:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.message || 'Failed to fetch delivery note data');
        } catch (e) {
          throw new Error('Failed to fetch delivery note data');
        }
      }
     
      const data = await response.json();
      console.log('Delivery note data received:', data);
      
      // Fetch tracking details for each item to determine tracking type
      if (data.Items && data.Items.length > 0) {
        const itemsWithTracking = await Promise.all(
          data.Items.map(async (item) => {
            try {
              const trackingDetails = await fetchItemTrackingDetails(deliveryNoteNumber, item.DeliveryNoteItemCode);
              
              // Determine tracking type based on tracking details
              let trackingType = 'none';
              if (trackingDetails.length > 0) {
                const hasBatchTracking = trackingDetails.some(detail => 
                  detail.TrackingType === 'batch' || detail.TrackingType === 1
                );
                const hasSerialTracking = trackingDetails.some(detail => 
                  detail.TrackingType === 'serial' || detail.TrackingType === 2
                );
                
                if (hasBatchTracking) {
                  trackingType = 'batch';
                } else if (hasSerialTracking) {
                  trackingType = 'serial';
                }
              }
              
              return {
                ...item,
                trackingType
              };
            } catch (error) {
              console.error('Error fetching tracking details for item:', item.DeliveryNoteItemCode, error);
              return {
                ...item,
                trackingType: 'none'
              };
            }
          })
        );
        
        // Update the delivery note data with items that have tracking type
        const updatedData = {
          ...data,
          Items: itemsWithTracking
        };
        
        setDeliveryNoteData(updatedData);
      } else {
        setDeliveryNoteData(data);
      }
     
      // Set customer information
      if (data.CustomerCode) {
        setCustomerInfo({
          customer_code: data.CustomerCode,
          customer_name: data.customer_name || 'N/A'
        });
      }
    } catch (err) {
      console.error('Error fetching delivery note data:', err);
      setError(err.message || 'Error loading delivery note data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrackingData = async (deliveryNoteNumber) => {
    setTrackingLoading(true);
    setTrackingError(null);
    try {
      const response = await axios.get(`${API_URL}/delivery-note-tracking/${deliveryNoteNumber}`);
      setTrackingData(response.data);
    } catch (error) {
      console.error('Error fetching tracking data:', error);
      setTrackingError('Failed to load tracking information');
    } finally {
      setTrackingLoading(false);
    }
  };
  
  // Helper function to safely get IsActive status
  const getIsActiveStatus = (isActive) => {
    if (Array.isArray(isActive)) {
      // If it's an array, use the first element
      return isActive[0] ? 'Active' : 'Inactive';
    }
    // If it's already a boolean or can be evaluated as boolean
    return isActive ? 'Active' : 'Inactive';
  };
  
  // Prepare permissions object to pass to DeliveryNoteTrackingTree
  const permissions = {
    canViewPO,
    canViewMRN,
    canViewDeliveryNote,
    canViewSales,
    showErrorPopup
  };
  
  if (!open) return null;
  
  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${
        open ? "opacity-100 visible" : "opacity-0 invisible"
      }`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out forwards;
          }
        `}
      </style>
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
      
      {/* Main Modal Container */}
      <div className={`bg-white shadow-2xl flex flex-col transition-all duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} ${isMaximized ? 'w-full h-full max-w-none m-0 rounded-none' : showTrackingPanel ? 'w-[90%] max-w-7xl' : 'w-full max-w-6xl'} h-full rounded-none`}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">
              Delivery Note Details
            </h2>
            <div className="flex gap-2">
              <button 
                className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
                  showTrackingPanel 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                }`}
                onClick={toggleTrackingPanel}
                title="Track Document"
              >
                <TrackDocumentIcon size={18} />
                <span>Track Document</span>
              </button>
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
              </button>
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1"
                onClick={onClose}
                title="Close"
              >
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Main Content Area with Sidebar Layout */}
        <div className="flex-grow overflow-hidden flex">
          {/* Main Delivery Note Details Content */}
          <div className={`flex-grow overflow-y-auto hide-scrollbar px-8 ${showTrackingPanel ? 'pr-4' : ''}`}>
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading delivery note data...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">
                      {error}
                      {error.includes('Delivery note not found') && (
                        <span className="block mt-2">
                          Please check if the delivery note number is correct and try again.
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            ) : deliveryNoteData ? (
              <div className="space-y-6 pb-6">
                {/* Delivery Note Header */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">Delivery Note Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Delivery Note Number</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                        {deliveryNoteData.DeliveryNoteNo}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Delivery Note Date</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                        {deliveryNoteData.DeliveryNoteDate ? new Date(deliveryNoteData.DeliveryNoteDate).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Due Date</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                        {deliveryNoteData.DueDate ? new Date(deliveryNoteData.DueDate).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Branch</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                       {deliveryNoteData.BranchCode || '-'} { deliveryNoteData.BranchName}
                      </div>
                    </div>

                    {/* Department in header (conditional) */}
                    { (shouldShowDepartmentOnHeader(screenConfig || deliveryNoteData?.screenConfig)) && (
                      <div className="flex flex-col gap-2 md:col-span-2">
                        <label className="block text-sm font-medium text-[#64748b]">Department</label>
                        <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                          {` ${deliveryNoteData.DepartmentCode || '-'}`}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Status</label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {getIsActiveStatus(deliveryNoteData.IsActive)}
                      </div>
                    </div>
                  </div>
                 
                  {/* Customer Information */}
                  {customerInfo && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="text-md font-semibold text-blue-800 mb-2">Customer Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <span className="text-sm text-gray-600">Customer Code:</span>
                          <span className="font-medium ml-2 uppercase">{customerInfo.customer_code}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Customer Name:</span>
                          <span className="font-medium ml-2 uppercase">{customerInfo.customer_name}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
               
                {/* Sales Order Selection */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-[#384959]">Sales Orders</h3>
                  </div>
                 
                  {/* Display selected sales orders */}
                  {deliveryNoteData.Items && deliveryNoteData.Items.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {[...new Set(deliveryNoteData.Items.map(item => item.SalesOrderNumber).filter(Boolean))].map(orderNumber => (
                          <span key={orderNumber} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            {orderNumber}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                 
                  {/* Delivery Note Items */}
                  <div className="mt-4">
                    <h4 className="text-md font-semibold text-[#384959] mb-2">Delivery Note Items</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full border border-[#E2E8F0]">
                        <thead>
                          <tr className="bg-[#BDDDFC] text-[#384959]">
                            <th className="p-2">Sales Order #</th>
                            <th className="p-2">Product</th>
                            <th className="p-2">Description</th>
                            <th className="p-2">UOM</th>
                            <th className="p-2">Quantity</th>
                            <th className="p-2">Unit Price</th>
                            <th className="p-2">Discount</th>
                            <th className="p-2">Tax</th>
                            <th className="p-2">Tax Rate</th>
                            <th className="p-2">Tax Amount</th>

                            {/* Conditional header for Department column on items */}
                            {shouldShowDepartmentOnItems(screenConfig || deliveryNoteData?.screenConfig) && (
                              <th className="p-2">Department</th>
                            )}

                            <th className="p-2">Total</th>
                            <th className="p-2">Tracking Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deliveryNoteData.Items && deliveryNoteData.Items.length > 0 ? (
                            deliveryNoteData.Items.map((item, idx) => (
                              <tr key={idx} className="border-t border-[#E2E8F0]">
                                <td className="p-2">
                                  <div className="w-24 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]">
                                    {item.SalesOrderNumber || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-32 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase">
                                    {item.ProductName || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-40 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase">
                                    {item.Description || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase">
                                    {item.UOMName || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]">
                                    {item.Quantity || '0'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]">
                                    {parseFloat(item.UnitPrice || 0).toFixed(2)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]">
                                    {parseFloat(item.Discount || 0).toFixed(2)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]">
                                    {item.TaxCode || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]">
                                    {item.TaxPercentage ? `${item.TaxPercentage}%` : '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9]">
                                    {parseFloat(item.TaxAmount || 0).toFixed(2)}
                                  </div>
                                </td>

                                {/* Conditional cell for Department per item */}
                                {shouldShowDepartmentOnItems(screenConfig || deliveryNoteData?.screenConfig) && (
                                  <td className="p-2">
                                    <div className="w-40 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] uppercase">
                                      {` ${item.DepartmentCode || '-'}`}
                                    </div>
                                  </td>
                                )}

                                <td className="p-2">
                                  <div className="w-20 border border-[#E2E8F0] rounded-lg px-2 py-1 bg-[#F1F5F9] font-semibold">
                                    {parseFloat(item.Total || 0).toFixed(2)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  {item.trackingType === 'batch' && (
                                    <button 
                                      className="text-blue-600 hover:text-blue-800 text-sm"
                                      onClick={() => {
                                        fetchItemTrackingDetails(deliveryNoteData.DeliveryNoteNo, item.DeliveryNoteItemCode).then(data => {
                                          console.log('Tracking details for batch item:', data);
                                          const batches = data.filter(detail => 
                                            detail.TrackingType === 'batch' || detail.TrackingType === 1
                                          );
                                          console.log('Filtered batches:', batches);
                                          handleViewBatches(batches, item.ProductName);
                                        });
                                      }}
                                    >
                                      View Batches
                                    </button>
                                  )}
                                  {item.trackingType === 'serial' && (
                                    <button 
                                      className="text-blue-600 hover:text-blue-800 text-sm"
                                      onClick={() => {
                                        fetchItemTrackingDetails(deliveryNoteData.DeliveryNoteNo, item.DeliveryNoteItemCode).then(data => {
                                          console.log('Tracking details for serial item:', data);
                                          const serials = data.filter(detail => 
                                            detail.TrackingType === 'serial' || detail.TrackingType === 2
                                          );
                                          const locationCode = serials.length > 0 ? 
                                            (serials[0].WarehouseCode || serials[0].LocationCode || item.WarehouseCode) : '';
                                          handleViewSerials(serials, item.ProductName, locationCode);
                                        });
                                      }}
                                    >
                                      View Serials
                                    </button>
                                  )}
                                  {!item.trackingType && (
                                    <span className="text-gray-500 text-sm">N/A</span>
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              {/* colSpan adjusts depending on whether Department column is visible */}
                              <td
                                colSpan={shouldShowDepartmentOnItems(screenConfig || deliveryNoteData?.screenConfig) ? 13 : 12}
                                className="text-center py-4 text-gray-500"
                              >
                                No items found for this delivery note.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                 
                  {/* Summary Section */}
                  <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                    <h4 className="text-md font-semibold text-[#384959] mb-4">Summary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Subtotal</span>
                        <span className="text-base font-semibold">{parseFloat(deliveryNoteData.SubTotal || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Total Tax</span>
                        <span className="text-base font-semibold">{parseFloat(deliveryNoteData.TotalTax || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Total Discount</span>
                        <span className="text-base font-semibold">{parseFloat(deliveryNoteData.Discount || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Shipping & Handling</span>
                        <span className="text-base font-semibold">{parseFloat(deliveryNoteData.ShippingHandlingCharge || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Total Amount</span>
                        <span className="text-base font-semibold text-lg text-blue-600">{parseFloat(deliveryNoteData.TotalAmount || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                 
                  {/* Payment Section */}
                  <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                    <h4 className="text-md font-semibold text-[#384959] mb-4">Payment Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Amount Paid</span>
                        <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                          {parseFloat(deliveryNoteData.AmountPaid || 0).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Balance Due</span>
                        <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                          {parseFloat(deliveryNoteData.BalanceDue || 0).toFixed(2)}
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Payment Method</span>
                        <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                          {deliveryNoteData.PaymentMethod || '-'}
                        </div>
                      </div>
                      <div className="flex flex-col md:col-span-2">
                        <span className="text-sm text-gray-500">Payment Notes</span>
                        <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] min-h-[60px]">
                          {deliveryNoteData.PaymentNotes || '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center h-64">
                <p className="text-gray-500">No delivery note data available</p>
              </div>
            )}
          </div>
          
          {/* Tracking Sidebar */}
          {showTrackingPanel && (
            <div className="border-l overflow-y-auto hide-scrollbar bg-white rounded-xl p-6 border border-gray-200 h-full" style={{width : "50vw"}}>
              <DeliveryNoteTrackingTree 
                data={trackingData}
                trackingLoading={trackingLoading}
                trackingError={trackingError}
                permissions={permissions}
                onDocumentClick={handleDocumentClick}
                onCloseTrackingPanel={() => setShowTrackingPanel(false)}
              />
            </div>
          )}
        </div>
        
        {/* Fixed Footer with Button */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      
      {/* Document Tab Modals */}
      {activeDocumentTab && activeDocumentTab.type === 'purchase' && (
        <PurchaseOrderViewModal
          open={!!activeDocumentTab}
          onClose={() => setActiveDocumentTab(null)}
          poId={activeDocumentTab}
        />
      )}
      
      {activeDocumentTab && activeDocumentTab.type === 'mrn' && (
        <MaterialReceiptViewModal
          open={!!activeDocumentTab}
          onClose={() => setActiveDocumentTab(null)}
          mrnId={activeDocumentTab}
        />
      )}
      
      {activeDocumentTab && activeDocumentTab.type === 'sales' && (
        <SalesOrderViewModal
          open={!!activeDocumentTab}
          onClose={() => setActiveDocumentTab(null)}
          soId={activeDocumentTab}
        />
      )}
      
      {activeDocumentTab && activeDocumentTab.type === 'deliveryNote' && (
        <InvoiceViewModal
          open={!!activeDocumentTab}
          onClose={() => setActiveDocumentTab(null)}
          deliveryNoteId={activeDocumentTab}
        />
      )}
      
      {/* Batch and Serial Info Modals */}
      <BatchInfoModal
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        batches={currentBatches}
        productName={currentProductName}
      />
      
      <SerialInfoModal
        isOpen={showSerialModal}
        onClose={() => setShowSerialModal(false)}
        serialNumbers={currentSerials}
        productName={currentProductName}
        locationCode={currentLocationCode}
      />
      
      {/* Modern Error Popup */}
      <ErrorPopup 
        message={errorPopup.message} 
        isVisible={errorPopup.isVisible} 
        onClose={hideErrorPopup} 
      />
    </div>
  );
};

export default InvoiceViewModal;